import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserPlan, PLAN_CONFIG, CREDIT_TOP_UPS, CREDIT_COSTS, PlanType } from '../../hooks/useUserPlan';
import { useCredits } from '../../hooks/useCredits';
import { useGenerationCount } from '../../hooks/useGenerationCount';
import { useBusinessProfiles } from '../../hooks/useBusinessProfiles';
import { useTeam } from '../../hooks/useTeam';
import { supabase } from '../../lib/supabase';
import { getEdgeFunctionHeaders } from '../../lib/edgeFunctions';

const PURCHASE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/purchase-credits`;

const PLAN_DETAILS = {
  free: {
    label: 'Free Plan',
    price: '$0 / mo',
    color: 'bg-stone-100 text-stone-600',
    borderActive: 'border-stone-300 ring-2 ring-stone-300',
    borderInactive: 'border-stone-200',
    badgeBg: 'bg-stone-100 text-stone-600',
    features: ['25 credits / month', '1 Business Profile', 'Facebook, Instagram, Google ads', '1 creative image per generation', 'Basic copy formats'],
  },
  pro: {
    label: 'Pro Plan',
    price: '$29 / mo',
    color: 'bg-orange-100 text-orange-600',
    borderActive: 'border-orange-400 ring-2 ring-orange-300',
    borderInactive: 'border-stone-200',
    badgeBg: 'bg-orange-100 text-orange-600',
    features: ['250 credits / month', 'Up to 3 Business Profiles', 'All platforms supported', '3 creative images per generation', 'Priority support', 'Ad angle & strategy controls'],
  },
  agency: {
    label: 'Agency Plan',
    price: '$99 / mo',
    color: 'bg-amber-100 text-amber-700',
    borderActive: 'border-amber-400 ring-2 ring-amber-300',
    borderInactive: 'border-stone-200',
    badgeBg: 'bg-amber-100 text-amber-700',
    features: ['1,000 credits / month', 'Unlimited Business Profiles', 'Client workspace support', '6 creative images per generation', 'White-label + API access', 'Dedicated account support'],
  },
};

const PLAN_ORDER: PlanType[] = ['free', 'pro', 'agency'];
const PLAN_RANK: Record<PlanType, number> = { free: 0, pro: 1, agency: 2 };

const ACTION_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  generate_ad:      { label: 'Ad Generation',       icon: 'ri-magic-line',         color: 'text-orange-500' },
  regenerate_image: { label: 'Image Regeneration',  icon: 'ri-refresh-line',       color: 'text-amber-500' },
  edit_image:       { label: 'Image Edit',           icon: 'ri-edit-line',          color: 'text-stone-500' },
  monthly_reset:    { label: 'Monthly Reset',        icon: 'ri-refresh-fill',       color: 'text-green-500' },
  top_up:           { label: 'Credit Top-Up',        icon: 'ri-add-circle-line',    color: 'text-green-500' },
  plan_upgrade:     { label: 'Plan Upgrade Bonus',   icon: 'ri-vip-crown-line',     color: 'text-orange-500' },
};

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-subscription`;

export default function SettingsPage() {
  const { user, signOut, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { plan } = useUserPlan();
  const { usedCount } = useGenerationCount();
  const { profiles } = useBusinessProfiles();
  const {
    creditsRemaining,
    creditLimit,
    creditPercent,
    creditsResetAt,
    daysUntilReset,
    freeEditsUsed,
    pendingPlan,
    loading: creditsLoading,
    recentTransactions,
    refetch,
  } = useCredits();

  const initialTab = searchParams.get('tab') as 'account' | 'plan' | 'credits' | 'team' | 'security' | null;
  const [activeTab, setActiveTab] = useState<'account' | 'plan' | 'credits' | 'team' | 'security'>(initialTab ?? 'account');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Plan switching state
  const [confirmPlan, setConfirmPlan] = useState<PlanType | null>(null);
  const [planSwitching, setPlanSwitching] = useState(false);
  const [planMsg, setPlanMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [cancellingPending, setCancellingPending] = useState(false);

  // Cancel membership state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellingMembership, setCancellingMembership] = useState(false);
  const [cancelMsg, setCancelMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Top-up state
  const [topUpLoading, setTopUpLoading] = useState<string | null>(null);
  const [topUpMsg, setTopUpMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const displayName = (user?.user_metadata?.full_name as string | undefined) ?? user?.email?.split('@')[0] ?? 'User';
  const email = user?.email ?? '';
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A';
  const planInfo = PLAN_DETAILS[plan as keyof typeof PLAN_DETAILS] ?? PLAN_DETAILS.free;

  // Team collaboration state
  const { team, members, invitations, loading: teamLoading, isOwner, inviteMember, removeMember, cancelInvitation, updateTeamName } = useTeam();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  const resetDateFormatted = creditsResetAt
    ? new Date(creditsResetAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setPwMsg({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    if (newPassword.length < 8) {
      setPwMsg({ type: 'error', text: 'Password must be at least 8 characters.' });
      return;
    }
    setPwSaving(true);
    setPwMsg(null);
    const { error } = await updatePassword(newPassword);
    setPwSaving(false);
    if (error) {
      setPwMsg({ type: 'error', text: error });
    } else {
      setPwMsg({ type: 'success', text: 'Password updated successfully.' });
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handlePlanChange = async (targetPlan: PlanType) => {
    if (!user) return;
    const isUpgrade = PLAN_RANK[targetPlan] > PLAN_RANK[plan as PlanType];
    const action = isUpgrade ? 'upgrade' : 'downgrade';

    setPlanSwitching(true);
    setPlanMsg(null);

    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({ action, newPlan: targetPlan }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error ?? 'Something went wrong.');
      }

      const msgType = isUpgrade ? 'success' : 'info';
      setPlanMsg({ type: msgType, text: json.message });
      setConfirmPlan(null);
      await refetch();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not process plan change.';
      setPlanMsg({ type: 'error', text: msg });
    } finally {
      setPlanSwitching(false);
    }
  };

  const handleCancelPending = async () => {
    if (!user) return;
    setCancellingPending(true);
    setPlanMsg(null);

    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({ action: 'cancel_pending' }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Could not cancel.');

      setPlanMsg({ type: 'success', text: json.message });
      await refetch();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not cancel plan change.';
      setPlanMsg({ type: 'error', text: msg });
    } finally {
      setCancellingPending(false);
    }
  };

  const handleCancelMembership = async () => {
    if (!user || plan === 'free') return;
    setCancellingMembership(true);
    setCancelMsg(null);

    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({ action: 'downgrade', newPlan: 'free' }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Could not cancel membership.');

      setCancelMsg({ type: 'success', text: `Your subscription has been cancelled. You'll keep ${planInfo.label} access until ${resetDateFormatted}, then move to the Free plan.` });
      setShowCancelConfirm(false);
      await refetch();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not cancel membership.';
      setCancelMsg({ type: 'error', text: msg });
    } finally {
      setCancellingMembership(false);
    }
  };

  const handleTopUp = async (packId: string) => {
    if (!user) return;
    setTopUpLoading(packId);
    setTopUpMsg(null);
    try {
      const res = await fetch(PURCHASE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({ packId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Could not start checkout.');
      window.location.href = json.url;
    } catch (err: unknown) {
      setTopUpMsg({ type: 'error', text: err instanceof Error ? err.message : 'Could not process purchase.' });
      setTopUpLoading(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || deleteConfirmText !== 'DELETE') return;
    setDeletingAccount(true);
    setDeleteMsg(null);

    try {
      // Delete user account via Supabase admin API
      const { error } = await supabase.rpc('delete_user');
      
      if (error) throw error;

      setDeleteMsg({ type: 'success', text: 'Your account has been permanently deleted. Redirecting...' });
      
      // Sign out and redirect after a brief delay
      setTimeout(async () => {
        await signOut();
        navigate('/');
      }, 2000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not delete account. Please contact support.';
      setDeleteMsg({ type: 'error', text: msg });
      setDeletingAccount(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    setInviteMsg(null);

    try {
      await inviteMember(inviteEmail, inviteRole);
      setInviteMsg({ type: 'success', text: `Invitation sent to ${inviteEmail}!` });
      setInviteEmail('');
      setInviteRole('member');
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteMsg(null);
      }, 2000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not send invitation';
      setInviteMsg({ type: 'error', text: msg });
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      await removeMember(memberId);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Could not remove member');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Could not cancel invitation');
    }
  };

  const handleUpdateTeamName = async () => {
    if (!newTeamName.trim()) return;
    
    try {
      await updateTeamName(newTeamName);
      setEditingTeamName(false);
      setNewTeamName('');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Could not update team name');
    }
  };

  const tabs = [
    { id: 'account',  label: 'Account',     icon: 'ri-user-line' },
    { id: 'plan',     label: 'Plan & Usage', icon: 'ri-vip-crown-line' },
    { id: 'credits',  label: 'Credits',      icon: 'ri-copper-coin-line' },
    { id: 'team',     label: 'Team',         icon: 'ri-team-line' },
    { id: 'security', label: 'Security',     icon: 'ri-shield-check-line' },
  ] as const;

  const inputClass = 'w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200';

  const barColor = creditPercent > 50 ? 'bg-gradient-to-r from-orange-400 to-amber-400'
    : creditPercent > 20 ? 'bg-gradient-to-r from-amber-400 to-yellow-400'
    : 'bg-gradient-to-r from-red-400 to-rose-400';

  return (
    <div className="flex-1 overflow-y-auto">
      <header className="h-16 bg-white border-b border-stone-100 flex items-center justify-between px-6 shrink-0 sticky top-0 z-10">
        <div>
          <h1 className="text-base font-bold text-stone-900">Settings</h1>
          <p className="text-xs text-stone-400">Manage your account and preferences</p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-stone-100 rounded-xl p-1 mb-6 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-stone-900' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── ACCOUNT TAB ── */}
        {activeTab === 'account' && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-sm font-bold text-stone-900 mb-5">Account Information</h2>
              <div className="flex items-center gap-5 mb-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-400 text-white text-xl font-bold shrink-0">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-base font-bold text-stone-900">{displayName}</div>
                  <div className="text-sm text-stone-500">{email}</div>
                  <div className="text-xs text-stone-400 mt-0.5">Member since {memberSince}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">Full Name</label>
                  <input value={displayName} readOnly className={`${inputClass} cursor-default`} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">Email Address</label>
                  <input value={email} readOnly className={`${inputClass} cursor-default`} />
                </div>
              </div>
              <p className="text-xs text-stone-400 mt-3">To update your email or name, please contact support.</p>
            </div>

            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-sm font-bold text-stone-900 mb-4">Danger Zone</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-3 border border-red-100 rounded-xl px-4">
                  <div>
                    <p className="text-sm font-semibold text-stone-800">Sign out of AdForge AI</p>
                    <p className="text-xs text-stone-400 mt-0.5">You'll need to sign back in to use the app.</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-1.5 text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-logout-box-r-line" />
                    Sign Out
                  </button>
                </div>

                <div className="rounded-xl border border-red-200 bg-red-50/40 p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold text-stone-800">Delete Account</p>
                      <p className="text-xs text-stone-500 mt-0.5">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                    </div>
                    {!showDeleteConfirm && (
                      <button
                        onClick={() => { setShowDeleteConfirm(true); setDeleteMsg(null); setDeleteConfirmText(''); }}
                        className="flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-white border border-red-300 hover:bg-red-50 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0"
                      >
                        <i className="ri-delete-bin-line" />
                        Delete Account
                      </button>
                    )}
                  </div>

                  {deleteMsg && (
                    <div className={`mt-4 flex items-start gap-2.5 px-4 py-3.5 rounded-xl border text-xs ${
                      deleteMsg.type === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-100 border-red-300 text-red-800'
                    }`}>
                      <i className={`text-sm shrink-0 mt-0.5 ${deleteMsg.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-600'}`} />
                      <span>{deleteMsg.text}</span>
                    </div>
                  )}

                  {showDeleteConfirm && (
                    <div className="mt-4 bg-white border border-red-300 rounded-xl p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-100 shrink-0">
                          <i className="ri-error-warning-line text-red-600 text-lg" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-stone-900">Are you absolutely sure?</p>
                          <p className="text-xs text-stone-600 mt-1 leading-relaxed">
                            This will permanently delete your account, including:
                          </p>
                          <ul className="text-xs text-stone-600 mt-2 space-y-1 ml-4 list-disc">
                            <li>All generated ad campaigns and creative assets</li>
                            <li>Business profiles and saved templates</li>
                            <li>Credit balance and transaction history</li>
                            <li>Account settings and preferences</li>
                          </ul>
                          <p className="text-xs font-bold text-red-600 mt-3">
                            This action is irreversible and cannot be undone.
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-xs font-semibold text-stone-700 mb-2">
                          Type <span className="font-mono bg-red-100 text-red-700 px-1.5 py-0.5 rounded">DELETE</span> to confirm:
                        </label>
                        <input
                          type="text"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="Type DELETE in capital letters"
                          className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-red-400 transition-all duration-200"
                          disabled={deletingAccount}
                        />
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                          className={`flex-1 flex items-center justify-center gap-2 text-xs font-bold px-5 py-2.5 rounded-xl transition-all duration-200 whitespace-nowrap ${
                            deletingAccount || deleteConfirmText !== 'DELETE'
                              ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                              : 'bg-red-600 hover:bg-red-700 text-white cursor-pointer'
                          }`}
                        >
                          {deletingAccount
                            ? <><i className="ri-loader-4-line animate-spin" /> Deleting account...</>
                            : <><i className="ri-delete-bin-line" /> Permanently delete my account</>
                          }
                        </button>
                        <button
                          onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteMsg(null); }}
                          disabled={deletingAccount}
                          className="flex-1 flex items-center justify-center gap-2 text-xs font-semibold px-5 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PLAN & USAGE TAB ── */}
        {activeTab === 'plan' && (
          <div className="space-y-5">

            {/* Pending downgrade banner */}
            {pendingPlan && (
              <div className="flex items-start justify-between gap-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-calendar-schedule-line text-amber-500 text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-800">Plan change scheduled</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      You&apos;ll move to the <span className="font-bold capitalize">{PLAN_DETAILS[pendingPlan as keyof typeof PLAN_DETAILS]?.label ?? pendingPlan}</span> on <span className="font-bold">{resetDateFormatted}</span>. Until then, you keep all your current {planInfo.label} features and credits.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCancelPending}
                  disabled={cancellingPending}
                  className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {cancellingPending ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-close-circle-line" />}
                  Cancel change
                </button>
              </div>
            )}

            {/* Feedback message */}
            {planMsg && (
              <div className={`flex items-start gap-3 px-5 py-4 rounded-2xl border text-sm ${
                planMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-800'
                : planMsg.type === 'info'    ? 'bg-stone-50 border-stone-200 text-stone-700'
                : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <i className={`text-base shrink-0 mt-0.5 ${
                  planMsg.type === 'success' ? 'ri-checkbox-circle-line text-green-500'
                  : planMsg.type === 'info'   ? 'ri-information-line text-stone-400'
                  : 'ri-error-warning-line text-red-500'
                }`} />
                <span>{planMsg.text}</span>
              </div>
            )}

            {/* Current plan at-a-glance */}
            <div className={`rounded-2xl border p-6 ${
              plan === 'agency' ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200' :
              plan === 'pro'    ? 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200' :
                                  'bg-white border-stone-100'
            }`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-widest font-semibold mb-1">Your Active Plan</p>
                  <h2 className="text-2xl font-bold text-stone-900">{planInfo.label}</h2>
                  <p className="text-sm text-stone-500 mt-0.5">
                    <span className="font-semibold">{creditLimit.toLocaleString()} credits / month</span>
                    &nbsp;· Renews <span className="font-semibold">{resetDateFormatted}</span>
                    {daysUntilReset > 0 && <span className="text-stone-400"> ({daysUntilReset}d away)</span>}
                  </p>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full shrink-0 ${planInfo.color}`}>
                  {plan === 'free' ? 'Free tier' : plan === 'pro' ? 'Pro tier' : 'Agency tier'}
                </span>
              </div>

              {/* Credit progress bar inline */}
              <div className="mb-2">
                <div className="flex items-center justify-between text-xs text-stone-500 mb-1.5">
                  <span>{creditsRemaining.toLocaleString()} credits remaining</span>
                  <span className="text-stone-400">{creditLimit - creditsRemaining} used</span>
                </div>
                <div className="w-full h-2 rounded-full bg-stone-200/70 overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${creditPercent}%` }} />
                </div>
              </div>
            </div>

            {/* Change Plan */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold text-stone-900">Change Plan</h2>
                {plan !== 'free' && (
                  <span className="text-xs text-stone-400 bg-stone-50 px-3 py-1 rounded-full border border-stone-100">
                    Billing date stays the same
                  </span>
                )}
              </div>
              <p className="text-xs text-stone-400 mb-5">
                Upgrades take effect immediately with a prorated charge and a fresh credit reset. Downgrades are scheduled for your next billing cycle — you keep everything until then.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {PLAN_ORDER.map(tier => {
                  const info = PLAN_DETAILS[tier];
                  const isCurrent = tier === plan;
                  const isPending = tier === pendingPlan;
                  const isUpgrade = PLAN_RANK[tier] > PLAN_RANK[plan as PlanType];
                  const isDowngrade = PLAN_RANK[tier] < PLAN_RANK[plan as PlanType];
                  const isConfirming = confirmPlan === tier;

                  return (
                    <div
                      key={tier}
                      className={`relative rounded-xl border p-5 flex flex-col gap-3 transition-all duration-200 ${
                        isCurrent ? info.borderActive + ' bg-gradient-to-b from-white to-stone-50/50' :
                        isPending  ? 'border-amber-300 bg-amber-50/30' :
                        isConfirming ? 'border-orange-300 bg-orange-50/20' :
                        info.borderInactive + ' hover:border-stone-300'
                      }`}
                    >
                      {isCurrent && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                          Current plan
                        </div>
                      )}
                      {isPending && !isCurrent && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                          Scheduled
                        </div>
                      )}

                      <div>
                        <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-full mb-2 ${info.badgeBg}`}>
                          {info.label}
                        </span>
                        <div className="text-xl font-bold text-stone-900">{info.price}</div>
                      </div>

                      <ul className="space-y-1.5 flex-1">
                        {info.features.map(f => (
                          <li key={f} className="flex items-start gap-2 text-xs text-stone-600">
                            <i className={`ri-check-line text-sm shrink-0 mt-0.5 ${isCurrent ? 'text-orange-400' : 'text-stone-400'}`} />
                            {f}
                          </li>
                        ))}
                      </ul>

                      {/* CTA area */}
                      <div className="pt-2 border-t border-stone-100">
                        {isCurrent ? (
                          <div className="flex items-center gap-1.5 text-xs text-stone-400 font-medium">
                            <i className="ri-shield-check-line" />
                            Active now
                          </div>
                        ) : isPending ? (
                          <div className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                            <i className="ri-time-line" />
                            Takes effect {resetDateFormatted}
                          </div>
                        ) : isConfirming ? (
                          <div className="space-y-2">
                            <p className="text-[11px] text-stone-600 font-medium leading-snug">
                              {isUpgrade
                                ? `Upgrade now? You'll be charged a prorated amount and your credits reset to ${PLAN_CONFIG[tier].creditLimit.toLocaleString()}.`
                                : `Downgrade on ${resetDateFormatted}? You'll keep your current features until then.`}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handlePlanChange(tier)}
                                disabled={planSwitching}
                                className={`flex-1 flex items-center justify-center gap-1 text-xs font-bold py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                                  isUpgrade
                                    ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
                                    : 'bg-stone-700 text-white hover:bg-stone-800'
                                }`}
                              >
                                {planSwitching ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmPlan(null)}
                                disabled={planSwitching}
                                className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setConfirmPlan(tier); setPlanMsg(null); }}
                            className={`w-full flex items-center justify-center gap-1.5 text-xs font-bold py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                              isUpgrade
                                ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
                                : 'border border-stone-300 text-stone-600 bg-white hover:bg-stone-50'
                            }`}
                          >
                            {isUpgrade ? (
                              <><i className="ri-arrow-up-circle-line" /> Upgrade — charged today</>
                            ) : (
                              <><i className="ri-arrow-down-circle-line" /> Switch on {resetDateFormatted}</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Billing info callout */}
              <div className="mt-5 flex items-start gap-3 bg-stone-50 border border-stone-100 rounded-xl px-4 py-3.5">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-information-line text-stone-400 text-base" />
                </div>
                <div className="text-xs text-stone-500 leading-relaxed">
                  <span className="font-semibold text-stone-700">Upgrade:</span> Charged a prorated amount for the rest of your current billing period. Credits reset immediately to the new plan&apos;s full allowance. Billing date stays unchanged.
                  <br />
                  <span className="font-semibold text-stone-700">Downgrade:</span> Scheduled for your next billing cycle ({resetDateFormatted}). Your current features and credits remain fully active until then.
                </div>
              </div>
            </div>

            {/* Credit Usage summary */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-stone-900">Usage Breakdown</h2>
                <span className="text-xs text-stone-400">This billing period</span>
              </div>
              {creditsLoading ? (
                <div className="flex items-center gap-2 text-sm text-stone-400 py-4">
                  <i className="ri-loader-4-line animate-spin" /> Loading usage...
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-100 text-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 mx-auto mb-2">
                      <i className="ri-magic-line text-orange-500 text-sm" />
                    </div>
                    <div className="text-2xl font-bold text-stone-900">{usedCount}</div>
                    <div className="text-xs text-stone-500 mt-0.5 leading-tight">Ads<br />Generated</div>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-100 text-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 mx-auto mb-2">
                      <i className="ri-briefcase-line text-amber-500 text-sm" />
                    </div>
                    <div className="text-2xl font-bold text-stone-900">{profiles.length}</div>
                    <div className="text-xs text-stone-500 mt-0.5 leading-tight">Business<br />Profiles</div>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-100 text-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-100 mx-auto mb-2">
                      <i className="ri-copper-coin-line text-green-500 text-sm" />
                    </div>
                    <div className="text-2xl font-bold text-stone-900">{creditLimit - creditsRemaining}</div>
                    <div className="text-xs text-stone-500 mt-0.5 leading-tight">Credits<br />Used</div>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4 border border-stone-100 text-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-200 mx-auto mb-2">
                      <i className="ri-time-line text-stone-500 text-sm" />
                    </div>
                    <div className="text-2xl font-bold text-stone-900">{daysUntilReset}</div>
                    <div className="text-xs text-stone-500 mt-0.5 leading-tight">Days Until<br />Reset</div>
                  </div>
                </div>
              )}
            </div>

            {/* Credit Cost Reference */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-sm font-bold text-stone-900 mb-4">Credit Cost Reference</h2>
              <div className="space-y-2">
                {[
                  { icon: 'ri-magic-line', color: 'bg-orange-50 text-orange-500', label: 'Generate Ad Campaign', sub: 'Standard ads start with one image', cost: `${CREDIT_COSTS.GENERATE_AD} credits` },
                  { icon: 'ri-sparkling-2-line', color: 'bg-sky-50 text-sky-500', label: 'Social Post', sub: 'Captions, hooks, CTAs, hashtags, and angle', cost: '3 credits' },
                  { icon: 'ri-image-line', color: 'bg-cyan-50 text-cyan-600', label: 'Social Post Image', sub: 'Optional AI image add-on', cost: '+5 credits' },
                  { icon: 'ri-movie-line', color: 'bg-rose-50 text-rose-500', label: 'Video Ad', sub: '5 sec = 50, 10 sec = 100, audio extra', cost: 'from 50 credits' },
                  { icon: 'ri-refresh-line', color: 'bg-amber-50 text-amber-500', label: 'Regenerate Single Image', sub: 'Swap one creative in a campaign', cost: `${CREDIT_COSTS.REGENERATE_IMAGE} credits` },
                  { icon: 'ri-edit-2-line', color: 'bg-teal-50 text-teal-500', label: 'Rewrite Ad Copy', sub: 'Refresh copy without changing images', cost: `${CREDIT_COSTS.REWRITE_COPY} credits` },
                  { icon: 'ri-edit-line', color: 'bg-stone-50 text-stone-500', label: 'Edit Ad Image', sub: `First ${CREDIT_COSTS.FREE_EDITS_ALLOWED} edits free per ad`, cost: `${CREDIT_COSTS.EDIT_IMAGE} credits` },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-stone-50 transition-colors duration-150">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${item.color}`}>
                        <i className={`${item.icon} text-sm`} />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-stone-800">{item.label}</div>
                        <div className="text-xs text-stone-400">{item.sub}</div>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-stone-600 whitespace-nowrap">{item.cost}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Cancel Membership */}
            {plan !== 'free' && (
              <div className="rounded-2xl border border-red-100 bg-red-50/40 p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="text-sm font-bold text-stone-900 mb-1">Cancel Subscription</h2>
                    <p className="text-xs text-stone-500 leading-relaxed max-w-md">
                      Cancelling schedules your plan to revert to <span className="font-semibold">Free</span> at the end of your current billing period (<span className="font-semibold">{resetDateFormatted}</span>). You keep all {planInfo.label} features and credits until then — nothing changes right away.
                    </p>
                  </div>
                  {!showCancelConfirm && (
                    <button
                      onClick={() => { setShowCancelConfirm(true); setCancelMsg(null); }}
                      className="flex items-center gap-2 text-xs font-semibold text-red-500 bg-white border border-red-200 hover:bg-red-50 px-4 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0"
                    >
                      <i className="ri-close-circle-line text-sm" />
                      Cancel Membership
                    </button>
                  )}
                </div>

                {cancelMsg && (
                  <div className={`mt-4 flex items-start gap-2.5 px-4 py-3.5 rounded-xl border text-xs ${
                    cancelMsg.type === 'success'
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    <i className={`text-sm shrink-0 mt-0.5 ${cancelMsg.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-500'}`} />
                    <span>{cancelMsg.text}</span>
                  </div>
                )}

                {showCancelConfirm && (
                  <div className="mt-5 bg-white border border-red-200 rounded-xl p-5">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-100 shrink-0">
                        <i className="ri-error-warning-line text-red-500 text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-stone-900">Are you sure you want to cancel?</p>
                        <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                          Your <span className="font-semibold">{planInfo.label}</span> will stay active until <span className="font-semibold">{resetDateFormatted}</span>. After that, you'll move to the Free plan with 25 credits/month and 1 business profile.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleCancelMembership}
                        disabled={cancellingMembership}
                        className="flex items-center justify-center gap-2 text-xs font-bold px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        {cancellingMembership
                          ? <><i className="ri-loader-4-line animate-spin" /> Cancelling...</>
                          : <><i className="ri-close-circle-line" /> Yes, cancel my plan</>
                        }
                      </button>
                      <button
                        onClick={() => { setShowCancelConfirm(false); setCancelMsg(null); }}
                        disabled={cancellingMembership}
                        className="flex items-center justify-center gap-2 text-xs font-semibold px-5 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        Keep my plan
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── CREDITS TAB ── */}
        {activeTab === 'credits' && (
          <div className="space-y-5">
            {/* Balance Card */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-sm font-bold text-stone-900">Credit Balance</h2>
                  <p className="text-xs text-stone-400 mt-0.5">Resets on {resetDateFormatted} · {daysUntilReset} days remaining</p>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full shrink-0 ${planInfo.color}`}>{planInfo.label}</span>
              </div>

              {creditsLoading ? (
                <div className="flex items-center gap-2 text-sm text-stone-400 py-4">
                  <i className="ri-loader-4-line animate-spin" /> Loading...
                </div>
              ) : (
                <>
                  <div className="flex items-end gap-2 mb-3">
                    <span className="text-4xl font-bold text-stone-900 leading-none">{creditsRemaining.toLocaleString()}</span>
                    <span className="text-base text-stone-400 mb-0.5">/ {creditLimit.toLocaleString()} credits</span>
                  </div>
                  <div className="w-full h-2.5 rounded-full bg-stone-100 overflow-hidden mb-1.5">
                    <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${creditPercent}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-stone-400">
                    <span>{Math.round(creditPercent)}% remaining</span>
                    {creditPercent <= 20 && (
                      <span className="text-red-500 font-semibold flex items-center gap-1">
                        <i className="ri-error-warning-line" /> Low — consider topping up
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Credit Cost Guide */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-sm font-bold text-stone-900 mb-4">Credit Costs</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2.5 border-b border-stone-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50">
                      <i className="ri-magic-line text-orange-500 text-sm" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-stone-800">Generate Ad Campaign</div>
                      <div className="text-xs text-stone-400">Full set of creatives per generation</div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-orange-500">{CREDIT_COSTS.GENERATE_AD} credits</span>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-stone-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
                      <i className="ri-refresh-line text-amber-500 text-sm" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-stone-800">Regenerate Single Image</div>
                      <div className="text-xs text-stone-400">Swap one creative in a campaign</div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-amber-500">{CREDIT_COSTS.REGENERATE_IMAGE} credits</span>
                </div>
                <div className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-50">
                      <i className="ri-edit-line text-stone-500 text-sm" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-stone-800">Edit Ad Image</div>
                      <div className="text-xs text-stone-400">First {CREDIT_COSTS.FREE_EDITS_ALLOWED} edits free, then {CREDIT_COSTS.EDIT_IMAGE} credits each</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-stone-500">{CREDIT_COSTS.EDIT_IMAGE} credits</span>
                    <div className="text-xs text-green-500 font-semibold">{CREDIT_COSTS.FREE_EDITS_ALLOWED} free / session</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-stone-400 bg-stone-50 rounded-xl px-4 py-3">
                <i className="ri-information-line shrink-0" />
                Free edits used this session:&nbsp;
                <span className="font-bold text-stone-700">{freeEditsUsed} / {CREDIT_COSTS.FREE_EDITS_ALLOWED}</span>
              </div>
            </div>

            {/* Top-Up Packs */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold text-stone-900">Top-Up Packs</h2>
                <span className="text-xs text-stone-400 bg-stone-50 px-2.5 py-1 rounded-full border border-stone-100">Credits never expire</span>
              </div>
              <p className="text-xs text-stone-400 mb-5">Need more credits before your reset? Buy a one-time pack — these stack on top of your monthly allowance.</p>

              {topUpMsg && (
                <div className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border text-xs mb-4 ${
                  topUpMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-800'
                  : topUpMsg.type === 'info'  ? 'bg-stone-50 border-stone-200 text-stone-700'
                  : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <i className={`text-sm shrink-0 mt-0.5 ${
                    topUpMsg.type === 'success' ? 'ri-checkbox-circle-line text-green-500'
                    : topUpMsg.type === 'info'  ? 'ri-information-line text-stone-400'
                    : 'ri-error-warning-line text-red-500'
                  }`} />
                  <span>{topUpMsg.text}</span>
                  <button onClick={() => setTopUpMsg(null)} className="ml-auto shrink-0 text-stone-400 hover:text-stone-600 cursor-pointer">
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {CREDIT_TOP_UPS.map(pack => {
                  const isLoading = topUpLoading === pack.id;
                  const isPopular = pack.id === 'growth';
                  return (
                    <div
                      key={pack.id}
                      className={`relative rounded-xl border p-5 flex flex-col gap-3 transition-all duration-200 ${
                        isPopular
                          ? 'border-orange-300 bg-orange-50/60'
                          : 'border-stone-200 bg-stone-50/60 hover:border-stone-300'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-amber-400 text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                          Most Popular
                        </div>
                      )}
                      {pack.saving && (
                        <span className="absolute top-3 right-3 text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {pack.saving}
                        </span>
                      )}
                      <div>
                        <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">{pack.label}</div>
                        <div className="text-2xl font-bold text-stone-900">{pack.credits.toLocaleString()}</div>
                        <div className="text-xs text-stone-400">credits</div>
                      </div>
                      <div className="text-xl font-bold text-stone-800">{pack.price} <span className="text-sm font-normal text-stone-400">one-time</span></div>
                      <button
                        onClick={() => handleTopUp(pack.id)}
                        disabled={topUpLoading !== null}
                        className={`w-full flex items-center justify-center gap-1.5 text-xs font-bold py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          isLoading
                            ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                            : isPopular
                            ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
                            : 'bg-stone-800 text-white hover:bg-stone-900'
                        }`}
                      >
                        {isLoading
                          ? <><i className="ri-loader-4-line animate-spin" /> Processing...</>
                          : <><i className="ri-shopping-cart-line" /> Buy {pack.label}</>}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-stone-400 mt-4 flex items-center gap-1.5">
                <i className="ri-secure-payment-line" />
                Payments processed securely via Stripe — your card details are never stored here
              </p>
            </div>

            {/* Transaction History */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-sm font-bold text-stone-900 mb-4">Recent Transactions</h2>
              {creditsLoading ? (
                <div className="flex items-center gap-2 text-sm text-stone-400 py-4">
                  <i className="ri-loader-4-line animate-spin" /> Loading transactions...
                </div>
              ) : recentTransactions.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-stone-50 text-stone-400 text-xl">
                    <i className="ri-file-list-3-line" />
                  </div>
                  <p className="text-sm text-stone-500 font-medium">No transactions yet</p>
                  <p className="text-xs text-stone-400">Your credit usage will appear here once you start generating ads.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentTransactions.map(tx => {
                    const meta = ACTION_LABELS[tx.action_type] ?? { label: tx.action_type, icon: 'ri-coin-line', color: 'text-stone-500' };
                    const isPositive = tx.amount > 0;
                    const date = new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    const time = new Date(tx.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div key={tx.id} className="flex items-center justify-between py-3 border-b border-stone-50 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-stone-50 shrink-0 ${meta.color}`}>
                            <i className={`${meta.icon} text-sm`} />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-stone-800">{meta.label}</div>
                            <div className="text-xs text-stone-400">{tx.description ?? `${date} · ${time}`}</div>
                          </div>
                        </div>
                        <span className={`text-sm font-bold ${isPositive ? 'text-green-500' : 'text-stone-700'}`}>
                          {isPositive ? '+' : ''}{tx.amount}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TEAM TAB ── */}
        {activeTab === 'team' && (
          <div className="space-y-5">
            {plan !== 'agency' ? (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-100 mx-auto mb-4">
                  <i className="ri-team-line text-amber-600 text-3xl" />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">Team Collaboration Available on Agency Plan</h3>
                <p className="text-sm text-stone-600 mb-5 max-w-md mx-auto">
                  Invite teammates, share business profiles, collaborate on ad campaigns, and operate under a unified credit pool.
                </p>
                <a
                  href="/pricing"
                  className="inline-flex items-center gap-2 text-sm font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-6 py-3 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-vip-crown-line" />
                  Upgrade to Agency Plan
                </a>
              </div>
            ) : teamLoading ? (
              <div className="flex items-center gap-2 text-sm text-stone-400 py-8">
                <i className="ri-loader-4-line animate-spin" /> Loading team...
              </div>
            ) : !team ? (
              <div className="bg-white border border-stone-100 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-stone-100 mx-auto mb-4">
                  <i className="ri-error-warning-line text-stone-400 text-2xl" />
                </div>
                <p className="text-sm text-stone-500">Team workspace not found. Please contact support.</p>
              </div>
            ) : (
              <>
                {/* Team Info */}
                <div className="bg-white rounded-2xl border border-stone-100 p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex-1">
                      <h2 className="text-sm font-bold text-stone-900 mb-1">Team Workspace</h2>
                      {editingTeamName ? (
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="text"
                            value={newTeamName}
                            onChange={(e) => setNewTeamName(e.target.value)}
                            placeholder={team.name}
                            className="text-sm px-3 py-2 rounded-lg border border-stone-200 bg-white text-stone-800 focus:outline-none focus:border-orange-400 transition-all duration-200"
                          />
                          <button
                            onClick={handleUpdateTeamName}
                            className="flex items-center gap-1 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-check-line" />
                            Save
                          </button>
                          <button
                            onClick={() => { setEditingTeamName(false); setNewTeamName(''); }}
                            className="flex items-center gap-1 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-lg font-bold text-stone-800">{team.name}</p>
                          {isOwner && (
                            <button
                              onClick={() => { setEditingTeamName(true); setNewTeamName(team.name); }}
                              className="flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-600 cursor-pointer"
                            >
                              <i className="ri-edit-line" />
                              Rename
                            </button>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-stone-400 mt-1">
                        {members.length} {members.length === 1 ? 'member' : 'members'} · Created {new Date(team.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => { setShowInviteModal(true); setInviteMsg(null); }}
                        className="flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-user-add-line" />
                        Invite Member
                      </button>
                    )}
                  </div>

                  <div className="bg-stone-50 border border-stone-100 rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-orange-100 shrink-0">
                      <i className="ri-information-line text-orange-500" />
                    </div>
                    <div className="text-xs text-stone-600 leading-relaxed">
                      <span className="font-bold">Shared credit pool:</span> All team members operate under the same credit balance and plan. Ad campaigns, business profiles, and generated content are accessible to all team members.
                    </div>
                  </div>
                </div>

                {/* Team Members */}
                <div className="bg-white rounded-2xl border border-stone-100 p-6">
                  <h3 className="text-sm font-bold text-stone-900 mb-4">Team Members ({members.length})</h3>
                  {members.length === 0 ? (
                    <div className="text-center py-6 text-sm text-stone-400">No team members yet</div>
                  ) : (
                    <div className="space-y-2">
                      {members.map((member) => (
                        <div key={member.id} className="flex items-center justify-between py-3 px-4 border border-stone-100 rounded-xl hover:border-stone-200 transition-all duration-200">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-amber-50 text-orange-500 font-bold text-sm shrink-0">
                              {member.email?.charAt(0).toUpperCase() ?? 'U'}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-stone-800">{member.email}</div>
                              <div className="text-xs text-stone-400">
                                {member.role === 'owner' ? '👑 Team Owner' : member.role === 'admin' ? '⭐ Admin' : 'Member'}
                                {member.joined_at && ` · Joined ${new Date(member.joined_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
                              </div>
                            </div>
                          </div>
                          {isOwner && member.role !== 'owner' && (
                            <button
                              onClick={() => handleRemoveMember(member.id)}
                              className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-user-unfollow-line" />
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pending Invitations */}
                {isOwner && invitations.length > 0 && (
                  <div className="bg-white rounded-2xl border border-stone-100 p-6">
                    <h3 className="text-sm font-bold text-stone-900 mb-4">Pending Invitations ({invitations.length})</h3>
                    <div className="space-y-2">
                      {invitations.map((invite) => (
                        <div key={invite.id} className="flex items-center justify-between py-3 px-4 border border-amber-100 bg-amber-50/50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-100 shrink-0">
                              <i className="ri-mail-send-line text-amber-600" />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-stone-800">{invite.email}</div>
                              <div className="text-xs text-stone-400">
                                {invite.role === 'admin' ? 'Admin' : 'Member'} · Invited {new Date(invite.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · Expires {new Date(invite.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleCancelInvitation(invite.id)}
                            className="flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 bg-white hover:bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-close-circle-line" />
                            Cancel
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invite Modal */}
                {showInviteModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                      <div className="flex items-center justify-between mb-5">
                        <h3 className="text-base font-bold text-stone-900">Invite Team Member</h3>
                        <button
                          onClick={() => { setShowInviteModal(false); setInviteMsg(null); setInviteEmail(''); }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-all duration-200 cursor-pointer"
                        >
                          <i className="ri-close-line text-lg" />
                        </button>
                      </div>

                      {inviteMsg && (
                        <div className={`flex items-start gap-2.5 px-4 py-3.5 rounded-xl border text-xs mb-4 ${
                          inviteMsg.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                          <i className={`text-sm shrink-0 mt-0.5 ${inviteMsg.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-500'}`} />
                          <span>{inviteMsg.text}</span>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">Email Address</label>
                          <input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="teammate@example.com"
                            className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                            disabled={inviting}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">Role</label>
                          <div className="flex gap-3">
                            <button
                              onClick={() => setInviteRole('member')}
                              className={`flex-1 py-3 px-4 rounded-xl border text-xs font-semibold transition-all duration-200 cursor-pointer ${
                                inviteRole === 'member'
                                  ? 'bg-orange-50 border-orange-300 text-orange-600'
                                  : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-stone-300'
                              }`}
                              disabled={inviting}
                            >
                              <div className="font-bold mb-0.5">Member</div>
                              <div className="text-[10px] text-stone-500">View and create ads</div>
                            </button>
                            <button
                              onClick={() => setInviteRole('admin')}
                              className={`flex-1 py-3 px-4 rounded-xl border text-xs font-semibold transition-all duration-200 cursor-pointer ${
                                inviteRole === 'admin'
                                  ? 'bg-orange-50 border-orange-300 text-orange-600'
                                  : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-stone-300'
                              }`}
                              disabled={inviting}
                            >
                              <div className="font-bold mb-0.5">Admin</div>
                              <div className="text-[10px] text-stone-500">Full team access</div>
                            </button>
                          </div>
                        </div>

                        <button
                          onClick={handleInviteMember}
                          disabled={inviting || !inviteEmail}
                          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                            !inviting && inviteEmail
                              ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                              : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                          }`}
                        >
                          {inviting ? (
                            <><i className="ri-loader-4-line animate-spin" /> Sending invitation...</>
                          ) : (
                            <><i className="ri-send-plane-fill" /> Send Invitation</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── SECURITY TAB ── */}
        {activeTab === 'security' && (
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h2 className="text-sm font-bold text-stone-900 mb-5">Change Password</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  className={inputClass}
                />
              </div>

              {pwMsg && (
                <div className={`flex items-center gap-2 text-sm px-4 py-3 rounded-xl ${
                  pwMsg.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-600'
                }`}>
                  <i className={pwMsg.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
                  {pwMsg.text}
                </div>
              )}

              <button
                onClick={handlePasswordChange}
                disabled={pwSaving || !newPassword || !confirmPassword}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                  !pwSaving && newPassword && confirmPassword
                    ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                    : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
              >
                {pwSaving ? <><i className="ri-loader-4-line animate-spin" /> Updating...</> : 'Update Password'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
